/**
 * Local-storage save & restore for partially-completed questionnaires.
 * See docs/superpowers/specs/2026-05-28-save-questionnaire-answers-design.md
 *
 * Stores the *minimum* needed to refill the user's answers on return:
 *   - a per-question structural signature (to detect BO changes)
 *   - the raw subset of answersSelectedMap that belongs to each question
 * No tree duplication. No uploaded files. No PII beyond what the user typed.
 */

import * as SessionStorageWrapper from "./SessionStorageWrapper";

// ----------------------------- types -----------------------------

export type AnswerValue = string | number | boolean;

export interface SavedQuestion {
  sig: string;
  values: Record<string, AnswerValue>;
}

export type SavedQuestionsMap = Record<string, SavedQuestion>;

export interface SavedQuestionnaireItem {
  key: string;
  countryID: string;
  categoryID: number;
  categoryMention: string;
  updatedAt: number;
  questions: SavedQuestionsMap;
}

export interface SavedQuestionnaireEnvelope {
  version: 1;
  items: SavedQuestionnaireItem[];
}

export interface PendingClearMarker {
  countryID: string;
  categoryID: number;
}

/**
 * Subset of the recipe shape we actually depend on. Mirrors what the
 * `/questionnaire/...` API returns. Wider fields exist at runtime — we
 * intentionally don't reference them.
 */
export interface AnswerRecipe {
  id: number;
  recipe_question_id: number;
  parent_id: number | null;
  type: string;
  order: number;
  validation_rule: unknown;
}

export interface AnswerTreeNode {
  recipe: AnswerRecipe;
  children: AnswerTreeNode[];
}

/** A question as it appears in `flattenedQuestionnaire`. */
export interface FlattenedQuestion {
  id: number | string;
  question_type: string;
  answersGroup?: AnswerTreeNode;
  additional?: AnswerTreeNode[];
}

/** One entry of the questionnaire API's `prefilledQuestionnaire` (customer defaults + previous order). */
export interface PrefilledAnswer {
  recipe: {
    id: number;
    type: string;
    recipe_answer_id: number | number[];
    value?: AnswerValue | null;
    recipe_question_id: number;
  };
}

// --------------------------- constants ---------------------------

const STORAGE_KEY = "questionnaire_answers:v1";
const PENDING_CLEAR_KEY = "questionnaire_answers:pending_clear";
const SKIP_QUESTIONNAIRE_ANSWERED_KEY_PREFIX = "skip_questionnaire_answered";
const MAX_SAVED_ITEMS = 5;
/** After this, a returning user starts from Q1 instead of resuming. */
export const QUESTIONNAIRE_SESSION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const UPLOAD_TYPES: ReadonlySet<string> = new Set(["photo_diagnose", "document_upload"]);

// ---------------------- public helpers / API ---------------------

export const isUploadAnswerType = (type: string | undefined | null): boolean => {
  return type != null && UPLOAD_TYPES.has(type);
};

export const getQuestionnaireStorageKey = (countryID: string, categoryID: number): string => {
  return `${countryID.toUpperCase()}:${categoryID}`;
};

/**
 * Tiny stable hash (djb2 → 32-bit unsigned → 8-char hex). Good enough to detect
 * structural changes; we never use it for security.
 */
export const djb2Hex8 = (input: string): string => {
  let hash = 5381;
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) + hash + input.charCodeAt(i)) | 0;
  }
  const unsigned = hash >>> 0;
  return unsigned.toString(16).padStart(8, "0");
};

/** Canonical, key-sorted JSON for stable hashing. */
const stableStringify = (value: unknown): string => {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(",")}}`;
};

interface SignatureNode {
  id: number;
  parent_id: number | null;
  type: string;
  order: number;
  validation_rule: unknown;
  children: SignatureNode[];
}

const buildSignatureNode = (node: AnswerTreeNode): SignatureNode => ({
  id: node.recipe.id,
  parent_id: node.recipe.parent_id,
  type: node.recipe.type,
  order: node.recipe.order,
  validation_rule: node.recipe.validation_rule ?? null,
  children: (node.children ?? []).map(buildSignatureNode),
});

/**
 * Build a stable 8-char signature representing the question's structural
 * shape (question id+type, plus every descendant answer's id/parent/type/order/
 * validation_rule). Two questionnaires whose structures match will produce the
 * same signature; any BO change to the question's structure breaks it.
 */
export const buildQuestionSignature = (question: FlattenedQuestion): string => {
  const groupNode = question.answersGroup ? buildSignatureNode(question.answersGroup) : null;
  const payload = {
    qid: question.id,
    qtype: question.question_type,
    group: groupNode,
  };
  return djb2Hex8(stableStringify(payload));
};

/**
 * Walk a tree node (or a list of tree roots) looking for an answer ID.
 * Returns null when not found.
 */
export const findAnswerNode = (
  root: AnswerTreeNode | undefined,
  targetID: number
): AnswerTreeNode | null => {
  if (!root) return null;
  if (root.recipe?.id === targetID) return root;
  for (const child of root.children ?? []) {
    const hit = findAnswerNode(child, targetID);
    if (hit) return hit;
  }
  return null;
};

/** Collect every recipe in a single tree node (depth-first), root included. */
const collectRecipesInTree = (node: AnswerTreeNode | undefined): AnswerRecipe[] => {
  const out: AnswerRecipe[] = [];
  const visit = (n: AnswerTreeNode | undefined): void => {
    if (!n) return;
    if (n.recipe) out.push(n.recipe);
    for (const child of n.children ?? []) visit(child);
  };
  visit(node);
  return out;
};

/**
 * Collect every recipe in the question's answer-group + additional tree
 * (depth-first). Used both when serializing answer values for save AND when
 * computing which keys to clear from `answersSelectedMap` before applying a
 * local restore on top of server prefill.
 */
export const collectAnswerRecipesForQuestion = (question: FlattenedQuestion): AnswerRecipe[] => {
  const out = collectRecipesInTree(question.answersGroup);
  for (const sub of question.additional ?? []) out.push(...collectRecipesInTree(sub));
  return out;
};

/**
 * Build `{ [questionId]: { sig, values } }` from current runtime state.
 * Drops questions that have no positive selections to keep the payload small.
 * Skips upload answers entirely.
 */
export const serializeQuestionnaireAnswers = (
  flattenedQuestionnaire: FlattenedQuestion[],
  answersSelectedMap: Record<string | number, AnswerValue | null | undefined>
): SavedQuestionsMap => {
  const out: SavedQuestionsMap = {};
  for (const question of flattenedQuestionnaire) {
    const values: Record<string, AnswerValue> = {};
    for (const recipe of collectAnswerRecipesForQuestion(question)) {
      if (isUploadAnswerType(recipe.type)) continue;
      const raw = answersSelectedMap[recipe.id];
      if (raw === undefined || raw === null) continue;
      // Skip explicit "false" — same effect as missing key on restore,
      // and dropping it keeps storage compact.
      if (raw === false) continue;
      if (typeof raw === "string" || typeof raw === "number" || typeof raw === "boolean") {
        values[String(recipe.id)] = raw;
      }
    }
    if (Object.keys(values).length === 0) continue;
    out[String(question.id)] = {
      sig: buildQuestionSignature(question),
      values,
    };
  }
  return out;
};

// ------------------------ envelope helpers -----------------------

const isAnswerValue = (v: unknown): v is AnswerValue => {
  return typeof v === "string" || typeof v === "number" || typeof v === "boolean";
};

const isSavedQuestion = (v: unknown): v is SavedQuestion => {
  if (!v || typeof v !== "object") return false;
  const sq = v as { sig?: unknown; values?: unknown };
  if (typeof sq.sig !== "string") return false;
  if (!sq.values || typeof sq.values !== "object") return false;
  for (const val of Object.values(sq.values as Record<string, unknown>)) {
    if (!isAnswerValue(val)) return false;
  }
  return true;
};

const isSavedQuestionnaireItem = (v: unknown): v is SavedQuestionnaireItem => {
  if (!v || typeof v !== "object") return false;
  const item = v as Partial<SavedQuestionnaireItem>;
  if (
    typeof item.key !== "string" ||
    typeof item.countryID !== "string" ||
    typeof item.categoryID !== "number" ||
    typeof item.categoryMention !== "string" ||
    typeof item.updatedAt !== "number" ||
    !item.questions ||
    typeof item.questions !== "object"
  ) {
    return false;
  }
  for (const q of Object.values(item.questions)) {
    if (!isSavedQuestion(q)) return false;
  }
  return true;
};

const readEnvelope = (): SavedQuestionnaireEnvelope => {
  if (typeof window === "undefined" || !window.localStorage) {
    return { version: 1, items: [] };
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { version: 1, items: [] };
    const parsed: unknown = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed !== "object" ||
      (parsed as { version?: unknown }).version !== 1 ||
      !Array.isArray((parsed as { items?: unknown }).items)
    ) {
      return { version: 1, items: [] };
    }
    const items = (parsed as { items: unknown[] }).items.filter(isSavedQuestionnaireItem);
    return { version: 1, items };
  } catch {
    return { version: 1, items: [] };
  }
};

const writeEnvelope = (envelope: SavedQuestionnaireEnvelope): void => {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(envelope));
  } catch {
    // Quota exceeded / private mode: silently drop.
  }
};

/** Remove the oldest entries when there are more than {@link MAX_SAVED_ITEMS}. */
const pruneItems = (items: SavedQuestionnaireItem[]): SavedQuestionnaireItem[] => {
  if (items.length <= MAX_SAVED_ITEMS) return items;
  return [...items].sort((a, b) => b.updatedAt - a.updatedAt).slice(0, MAX_SAVED_ITEMS);
};

// ----------------------------- save ------------------------------

export interface SaveQuestionnaireAnswersArgs {
  countryID: string;
  categoryID: number;
  categoryMention: string;
  questions: SavedQuestionsMap;
}

/**
 * Upsert the saved entry for (countryID, categoryID). If `questions` is empty,
 * the entry is removed instead — keeps the envelope free of empty rows.
 */
export const saveQuestionnaireAnswers = (args: SaveQuestionnaireAnswersArgs): void => {
  const { countryID, categoryID, categoryMention, questions } = args;
  if (!countryID || !Number.isFinite(categoryID)) return;

  const key = getQuestionnaireStorageKey(countryID, categoryID);
  const envelope = readEnvelope();
  const otherItems = envelope.items.filter((item) => item.key !== key);

  if (Object.keys(questions).length === 0) {
    writeEnvelope({ version: 1, items: otherItems });
    return;
  }

  const nextItem: SavedQuestionnaireItem = {
    key,
    countryID: countryID.toUpperCase(),
    categoryID,
    categoryMention,
    updatedAt: Date.now(),
    questions,
  };

  writeEnvelope({ version: 1, items: pruneItems([nextItem, ...otherItems]) });
};

// ---------------------------- load -------------------------------

export interface LoadQuestionnaireAnswersArgs {
  countryID: string;
  categoryID: number;
}

export const loadQuestionnaireAnswersItem = (
  args: LoadQuestionnaireAnswersArgs
): SavedQuestionnaireItem | null => {
  const { countryID, categoryID } = args;
  if (!countryID || !Number.isFinite(categoryID)) return null;
  const key = getQuestionnaireStorageKey(countryID, categoryID);
  const envelope = readEnvelope();
  return envelope.items.find((item) => item.key === key) ?? null;
};

export const loadQuestionnaireAnswers = (
  args: LoadQuestionnaireAnswersArgs
): SavedQuestionsMap | null => {
  const match = loadQuestionnaireAnswersItem(args);
  return match ? match.questions : null;
};

/**
 * Synchronous pre-load check: does this country have any unexpired saved
 * progress? The category id is only known once the questionnaire GET
 * resolves, so this cannot be exact — it lets the caller show a loader
 * instead of the intro while the real restore (computeRestorePlan) runs.
 */
export const hasSavedQuestionnaireProgress = (countryID: string | undefined): boolean => {
  if (!countryID) return false;
  const prefix = `${countryID.toUpperCase()}:`;
  const now = Date.now();
  return readEnvelope().items.some(
    (item) => item.key.startsWith(prefix) && now - item.updatedAt <= QUESTIONNAIRE_SESSION_TTL_MS
  );
};

// ---------------------------- clear ------------------------------

export const clearQuestionnaireAnswers = (countryID: string, categoryID: number): void => {
  if (!countryID || !Number.isFinite(categoryID)) return;
  const key = getQuestionnaireStorageKey(countryID, categoryID);
  const envelope = readEnvelope();
  const next = envelope.items.filter((item) => item.key !== key);
  if (next.length === envelope.items.length) return;
  writeEnvelope({ version: 1, items: next });
};

// --------------------------- restore -----------------------------

export interface RestorePlan {
  /** Every answer key in restored questions' subtrees — cleared before applying. */
  idsToClear: number[];
  /** Saved values resolved to their fresh tree nodes (upload answers excluded). */
  valuesToApply: Array<{ node: AnswerTreeNode; value: AnswerValue }>;
}

/**
 * Restore planner: diff the saved answers for (countryID, categoryID)
 * against the freshly-loaded questionnaire and produce the set of keys to clear
 * plus the values to re-apply. Returns null when there is nothing to restore
 * (no saved entry, expired > 30 days, or no restorable question matched).
 * Expired entries are deleted from localStorage. The caller applies the plan
 * to component state.
 *
 * For each saved question whose structural signature still matches the loaded
 * questionnaire, every answer key in that question's subtree is queued for
 * clearing (so server-prefilled siblings/checklist options don't leak through),
 * and each saved value is resolved to its fresh tree node. Upload answers are
 * skipped (file refs aren't persisted).
 */
export const computeRestorePlan = (
  countryID: string,
  categoryID: number,
  freshFlattened: FlattenedQuestion[],
  freshTreeRoots: Record<string | number, AnswerTreeNode>
): RestorePlan | null => {
  const savedItem = loadQuestionnaireAnswersItem({ countryID, categoryID });
  if (!savedItem) return null;
  if (Date.now() - savedItem.updatedAt > QUESTIONNAIRE_SESSION_TTL_MS) {
    clearQuestionnaireAnswers(countryID, categoryID);
    return null;
  }
  const saved = savedItem.questions;

  const idsToClear: number[] = [];
  const valuesToApply: Array<{ node: AnswerTreeNode; value: AnswerValue }> = [];

  for (const question of freshFlattened) {
    const savedQuestion = saved[String(question.id)];
    if (!savedQuestion) continue;
    if (buildQuestionSignature(question) !== savedQuestion.sig) continue;

    for (const recipe of collectAnswerRecipesForQuestion(question)) {
      idsToClear.push(recipe.id);
    }

    const tree: AnswerTreeNode | undefined = freshTreeRoots[question.id];
    if (!tree) continue;

    for (const [answerIDStr, value] of Object.entries(savedQuestion.values)) {
      const answerID = Number(answerIDStr);
      if (!Number.isFinite(answerID)) continue;
      const node = findAnswerNode(tree, answerID);
      if (!node) continue;
      if (isUploadAnswerType(node.recipe.type)) continue;
      valuesToApply.push({ node, value });
    }
  }

  if (idsToClear.length === 0) return null;

  return { idsToClear, valuesToApply };
};

/**
 * HL-12059: the same plan shape built from the server's `prefilledQuestionnaire`, for a returning
 * patient who skipped with "nothing changed" and came back. Value rules mirror Questionnaire.tsx's
 * `prefillQuestionnaire`. Answers missing from the fresh tree, uploads and the health_condition
 * question are left out; for the same radio group or field, the later entry (previous order) wins.
 */
export const computePrefillPlan = (
  prefilled: PrefilledAnswer[] | null | undefined,
  freshFlattened: FlattenedQuestion[],
  freshTreeRoots: Record<string | number, AnswerTreeNode>
): RestorePlan | null => {
  if (!prefilled?.length) return null;

  const valuesByKey = new Map<string, { node: AnswerTreeNode; value: AnswerValue }>();
  const questionIDs = new Set<string>();

  for (const { recipe } of prefilled) {
    if (!recipe) continue;
    const questionID = String(recipe.recipe_question_id);
    const question = freshFlattened.find((q) => String(q.id) === questionID);
    const tree = freshTreeRoots[questionID];
    if (!question || !tree || question.question_type === "health_condition") continue;

    let targets: Array<{ id: number; value: AnswerValue | null | undefined }>;
    if (["textarea", "date", "number", "text"].includes(recipe.type)) {
      targets = [{ id: Number(recipe.recipe_answer_id), value: recipe.value }];
    } else if (recipe.type === "check_list") {
      const ids = Array.isArray(recipe.recipe_answer_id) ? recipe.recipe_answer_id : [];
      targets = ids.map((id) => ({ id: Number(id), value: true }));
    } else {
      targets = [{ id: Number(recipe.id), value: true }];
    }

    for (const { id, value } of targets) {
      if (value === null || value === undefined || value === "") continue;
      const node = findAnswerNode(tree, id);
      if (!node || isUploadAnswerType(node.recipe.type)) continue;
      const isOption = value === true && recipe.type !== "check_list";
      valuesByKey.set(isOption ? `group:${node.recipe.parent_id}` : `answer:${id}`, { node, value });
      questionIDs.add(questionID);
    }
  }

  if (valuesByKey.size === 0) return null;

  const idsToClear: number[] = [];
  for (const question of freshFlattened) {
    if (!questionIDs.has(String(question.id))) continue;
    for (const answerRecipe of collectAnswerRecipesForQuestion(question)) idsToClear.push(answerRecipe.id);
  }

  return { idsToClear, valuesToApply: Array.from(valuesByKey.values()) };
};

// ----------------------- pending-clear marker --------------------

/**
 * Drop a marker so the Thankyou page can locate the right saved entry to
 * delete once the order is actually placed. SessionStorage is per-tab and
 * survives the navigation between questionnaire submit and order completion.
 */
export const markPendingClear = (countryID: string, categoryID: number): void => {
  if (!countryID || !Number.isFinite(categoryID)) return;
  const payload: PendingClearMarker = { countryID: countryID.toUpperCase(), categoryID };
  try {
    SessionStorageWrapper.setItem(PENDING_CLEAR_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }
};

/**
 * Read & delete the pending-clear marker. Returns null when there isn't one
 * (or it was malformed).
 */
export const consumePendingClear = (): PendingClearMarker | null => {
  let raw: string | null | undefined;
  try {
    raw = SessionStorageWrapper.getItem(PENDING_CLEAR_KEY);
  } catch {
    return null;
  }
  if (!raw || typeof raw !== "string") return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const candidate = parsed as Partial<PendingClearMarker>;
    if (typeof candidate.countryID !== "string" || typeof candidate.categoryID !== "number") {
      return null;
    }
    SessionStorageWrapper.removeItem(PENDING_CLEAR_KEY);
    return { countryID: candidate.countryID, categoryID: candidate.categoryID };
  } catch {
    return null;
  }
};

// ------------- per-tab-session re-entry markers (HL-12059) -------------

/**
 * The skip-questionnaire ("has your health changed?") popup is asked once per tab session, per catalog.
 * `skipped`: the customer answered "nothing changed" and the skip went through (vs. "something changed").
 */
export const markSkipQuestionnaireAnsweredThisSession = (categoryID: number | null | undefined, skipped: boolean): void => {
  if (!categoryID) return;
  try {
    SessionStorageWrapper.setItem(`${SKIP_QUESTIONNAIRE_ANSWERED_KEY_PREFIX}:${categoryID}`, skipped ? "skipped" : "changed");
  } catch {
    // ignore
  }
};

const readSkipQuestionnaireAnswer = (categoryID: number | null | undefined): string | null => {
  if (!categoryID) return null;
  try {
    return SessionStorageWrapper.getItem(`${SKIP_QUESTIONNAIRE_ANSWERED_KEY_PREFIX}:${categoryID}`) ?? null;
  } catch {
    return null;
  }
};

export const wasSkipQuestionnaireAnsweredThisSession = (categoryID: number | null | undefined): boolean =>
  readSkipQuestionnaireAnswer(categoryID) !== null;

export const wasQuestionnaireSkippedThisSession = (categoryID: number | null | undefined): boolean =>
  readSkipQuestionnaireAnswer(categoryID) === "skipped";
